/* WebComponentPartnerWizard component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Prop, State, Watch, Build } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { AbstractComponent } from '@cewe-designsystem/abstract';
import '@cewe-designsystem/component_modal';
import PartnerWizardTemplate from './templates/cwc-partner-wizard.template';
import Wizard, { WizardData } from './wizard';
import OrderChannelResolver from './resolver/order-channel-resolver';
import TradingPartnerResolver, { OrderChannels, TradingPartner } from './resolver/trading-partner-resolver';
import { EditorLinkClicked, ModalOpened, PartnerSelected, SoftwareLinkClicked } from './types/events';
import { ButtonVariant, ButtonsDirection } from './types/buttons';
import { ORDER_CHANNEL_KEY } from './types/orderChannels';

type PartnerWizardRenderState = {
  currentStep: 'channels' | 'partners' | null;
  partnerId: number | null;
};

/**
 * @part button-order-online - direct order button for online order.
 * @part button-order-software - direct order button for hps/mps.
 * @part modal-button-order-software-hps - modal order button for hps.
 * @part modal-button-order-software-mps - modal order button for mps.
 * @part modal-button-order-online - modal order button for onlien order.
 * @part button-self - button to open the modal with own orderchannels.
 * @part button-partners - button to open the modal with partner selection.
 */
@Component({
  tag: 'cwc-partner-wizard',
  styleUrl: './styles/cwc-partner-wizard.scss',
  shadow: true,
})
export class PartnerWizardComponent {
  @Element() element: HTMLElement;

  /**
   * helios product ID
   */
  @Prop()
  productId: number;

  /**
   * phx keyaccount ID
   */
  @Prop()
  keyAccount: number;

  /**
   * phoenix project id
   */
  @Prop()
  project: string;

  /**
   * language used to determine order channels
   */
  @Prop()
  language: string = 'de';

  /**
   * country used to determine order channels
   */
  @Prop() country: string = 'DE';

  /**
   * api endpoint path to get product trading partner order channels
   */
  @Prop() apiOrderChannelsPath: string;

  /**
   * change the position of the direct order buttons default is online than software
   */
  @Prop() availableKeyAccountsJson: string | null = null;

  /**
   * blocking specific order channels (hps,ips,mps)
   */
  @Prop() blockedOrderChannels: string | null = null;

  /**
   * the link for the download page of the hps software
   */
  @Prop() downloadPageLink: string = '';

  /**
   * (optional) override the own ips order channel link
   */
  @Prop() overrideOwnIpsLink: string | null = null;

  /**
   * (optional) when the session allows a mapping then the mapKeyAccount will be used on the own key account
   */
  @Prop() mapKeyAccount: number | null;

  /**
   * (optional) link to a page for more trading partners
   */
  @Prop() hrefMorePartnersButton: string;

  /**
   * (optional) force the component to render order channels in modal even if there is only one partner
   */
  @Prop() forceModalView: boolean;

  /**
   * (optional) change direction or reverse position of the root buttons
   */
  @Prop() buttonsDirection: ButtonsDirection = 'row';

  /**
   * (optional) reverse the order of the direct order buttons
   */
  @Prop() buttonsDirectOrderReversed: boolean = false;

  /**
   * (optional) style of the order at self button
   */
  @Prop() buttonSelfVariant: ButtonVariant = 'strong';

  /**
   * (optional) style of the order at partners button
   */
  @Prop() buttonPartnersVariant: ButtonVariant = 'strong-secondary';

  /**
   * (optional) style of the direct order online button
   */
  @Prop() buttonOrderOnlineVariant: ButtonVariant = 'strong';

  /**
   * (optional) style of the direct order software button
   */
  @Prop() buttonOrderSoftwareVariant: ButtonVariant = 'strong';

  /**
   * text for fallback if no order channel could found
   */
  @Prop() textFallback: string;

  /**
   * text for the order online button shown as direct order button
   */
  @Prop() textOrderOnlineButton: string;

  /**
   * text for the order via HPS button shown as direct order button
   */
  @Prop() textOrderHpsButton: string;

  /**
   * text for the order via MPS button shown as direct order button
   */
  @Prop() textOrderMpsButton: string;

  /** t
   * he order button to open the modal with order channels for the own keyaccount
   */
  @Prop() textSelfButton: string;

  /**
   * subline text for SelfButton
   */
  @Prop() textSelfButtonSubline: string; // html markup possible

  /**
   * text for the button to open the tradingpartner modal, order via partners
   */
  @Prop() textPartnersButton: string;

  /**
   * subline text for PartnersButton
   */
  @Prop() textPartnersButtonSubline: string; // html markup possible

  /**
   * Headline text for the partner selection modal
   */
  @Prop() textPartnersModalHeadline: string;

  /**
   * Description text for the partner selection modal
   */
  @Prop() textPartnersModalDescription: string;

  /**
   * text for the button shown in the modal under the selectable partners to show more partners
   */
  @Prop() textMorePartnersButton: string;

  /**
   * headline text for the modal showing the order channels of a keyaccount
   */
  @Prop() textChannelsModalHeadline: string;

  /**
   * headline text for the HPS order channel section in the modal
   */
  @Prop() textChannelsHpsHeadline: string;

  /**
   * subline text for the HPS order channel section in the modal
   */
  @Prop() textChannelsHpsSubline: string;

  /**
   * Selling points text for the HPS order channel section in the modal
   */
  @Prop() textChannelsHpsSellingPoints: string; // html markup possible

  /**
   * button text for the HPS order channel in the modal
   */
  @Prop() textChannelsHpsButton: string;

  /**
   * headline text for the MPS order channel section in the modal
   */
  @Prop() textChannelsMpsHeadline: string;

  /**
   * subline text for the MPS order channel section in the modal
   */
  @Prop() textChannelsMpsSubline: string;

  /**
   * Selling points text for the MPS order channel section in the modal
   */
  @Prop() textChannelsMpsSellingPoints: string; // html markup possible

  /**
   * button text for the MPS order channel in the modal
   */
  @Prop() textChannelsMpsButton: string;

  /**
   * headline text for the online order channel section in the modal
   */
  @Prop() textChannelsOnlineHeadline: string;

  /**
   * subline text for the online order channel section in the modal
   */
  @Prop() textChannelsOnlineSubline: string;

  /**
   * Selling points text for the online order channel section in the modal
   */
  @Prop() textChannelsOnlineSellingPoints: string; // html markup possible

  /**
   * button text for the online order channel in the modal
   */
  @Prop() textChannelsOnlineButton: string;

  @State() renderState: PartnerWizardRenderState = {
    currentStep: undefined,
    partnerId: 0,
  };

  /**
   * Event thrown when the user clicked on button for partner or own channel selection
   *
   * @docs
   * @event on:partner-wizard:modal-opened
   */
  @Event({
    eventName: 'on:partner-wizard:modal-opened',
  })
  onModalOpened: EventEmitter<ModalOpened>;

  /**
   * Event thrown when the user selected a partner in the partner selection
   *
   * @docs
   * @event on:partner-wizard:partner-selected
   */
  @Event({
    eventName: 'on:partner-wizard:partner-selected',
  })
  onPartnerSelected: EventEmitter<PartnerSelected>;

  /**
   * Event thrown when the user clicked on the hps/mps link to download the software
   *
   * @docs
   * @event on:partner-wizard:software-link-clicked
   */
  @Event({
    eventName: 'on:partner-wizard:software-link-clicked',
  })
  onSoftwareLinkClicked: EventEmitter<SoftwareLinkClicked>;

  /**
   * Event thrown when the user clicked on the ips link to open the editor
   *
   * @docs
   * @event on:partner-wizard:editor-link-clicked
   */
  @Event({
    eventName: 'on:partner-wizard:editor-link-clicked',
  })
  onEditorLinkClicked: EventEmitter<EditorLinkClicked>;

  wizardData: WizardData;

  /**
   * the internal modal to show order channels
   */
  modal: HTMLCwcComponentModalElement;

  orderChannelResolver: OrderChannelResolver;

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It's important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    //validate blocked order channels
    if (this.blockedOrderChannels) {
      const types = [
        ORDER_CHANNEL_KEY.DESKTOP_SOFTWARE,
        ORDER_CHANNEL_KEY.MOBILE_SOFTWARE,
        ORDER_CHANNEL_KEY.ONLINE_EDITOR,
      ];

      if (
        !this.blockedOrderChannels
          .split(',')
          .every((blockedOrderChannel) => types.includes(blockedOrderChannel as ORDER_CHANNEL_KEY))
      ) {
        throw new Error('blockedOrderChannels doesnt match the specified order channel keys');
      }
    }
  }

  /**
   * Called every time the component is disconnected from the DOM, ie, it call be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {
    this.setOrderChannelResolver();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    this.element.shadowRoot.querySelector('cwc-component-modal')?.addEventListener('on:modal:close', () => {
      this.closeModal();
    });
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {
    return this.loadWizardData();
  }

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It's never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * sets a new OrderChannelResolver if any of the related config attributes changed.
   */
  @Watch('apiOrderChannelsPath')
  @Watch('productId')
  @Watch('keyAccount')
  @Watch('project')
  @Watch('language')
  @Watch('country')
  setOrderChannelResolver() {
    if (Build.isDev) {
      console.debug('create new OrderChannelResolver');
    }
    this.orderChannelResolver = new OrderChannelResolver(
      this.apiOrderChannelsPath,
      this.productId,
      this.keyAccount,
      this.project,
      this.language,
      this.country,
    );
  }

  async loadWizardData() {
    if (Build.isDev) {
      console.debug('loadWizardData');
    }

    const tradingPartnerResolver = new TradingPartnerResolver(
      this.orderChannelResolver,
      this.availableKeyAccountsJson ? JSON.parse(this.availableKeyAccountsJson) : [],
      (this.blockedOrderChannels?.split(',') ?? []) as ORDER_CHANNEL_KEY[],
      this.language,
    );

    const wizard = new Wizard(
      tradingPartnerResolver,
      this.keyAccount,
      this.productId,
      this.downloadPageLink,
      this.mapKeyAccount,
      this.overrideOwnIpsLink,
    );

    const data = await wizard.get();
    this.wizardData = data;
  }

  async setStep(currentStep, partnerId: number | null = null) {
    this.renderState = { currentStep, partnerId };
    if (Build.isDev) {
      console.debug('setStep', this.renderState);
    }

    await this.openModal();
  }

  async openModal() {
    await this.modal.open();
  }

  async closeModal() {
    await this.modal.close();
  }

  getPartnerData(): TradingPartner | null {
    if (!this.renderState.partnerId) {
      return null;
    }

    return this.wizardData.tradingPartners.find((partner) => partner.tpAccountId === this.renderState.partnerId);
  }

  getCurrentKeyAccount(): number {
    const partner = this.getPartnerData();

    if (partner) {
      return partner.tpAccountId;
    }

    return this.wizardData.keyAccount;
  }

  getOrderChannels(): OrderChannels {
    return this.getPartnerData()?.orderChannels ?? this.wizardData.orderChannels;
  }

  /**
   * render
   */
  render() {
    return <PartnerWizardTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(PartnerWizardComponent.prototype, AbstractComponent.prototype);
export interface PartnerWizardComponent extends AbstractComponent {}
